import { PAGE_SIZE } from '../../common/constants';
import type {
	Priority,
	PaginatedPrioritiesData,
	MovePrioritiesRequestParams,
	ReorderParams,
} from '../../common/types';

type CreateMoveParams = {
	priorityId: string;
	newPageNumber: number;
} & (
	| { position: 'First' | 'Last' } // Move the priorities to the beginning or end
	| { after: string } // Move the priorities after the specified priority ID
);

const generateMoveRequestParams = ({ priorityId, newPageNumber, ...rest }: CreateMoveParams) => ({
	requestParams: {
		ids: [priorityId],
		...('position' in rest ? { position: rest.position } : { after: rest.after }),
	},
	newPageNumber,
});

/**
 * Convert a page number + position on the page to a zero-based index of the position in the full list
 * @returns
 */
const toRawIndex = (pageNumber: number, pageIndex: number) =>
	(pageNumber - 1) * PAGE_SIZE + pageIndex;

/**
 * Convert the index of an item in the full list to a page number and position on the page
 */
const toPageNumberAndIndex = (rawIndex: number) => {
	const pageNumber = Math.floor(rawIndex / PAGE_SIZE) + 1;
	const pageIndex = rawIndex % PAGE_SIZE;
	return { pageNumber, pageIndex };
};

/**
 * Get the raw index (zero-based index representing the position of the item in the full list).
 * This is used to get the the position of the item that is directly before the move destination and is required as the priority API requires the ID of this item.
 */
export const getRawIndexOfPreviousItem = ({
	reorderParams,
	paginatedPrioritiesData,
	currentPageNumber,
}: {
	reorderParams: ReorderParams;
	currentPageNumber: number;
	paginatedPrioritiesData: PaginatedPrioritiesData;
}) => {
	const { action, priorityId } = reorderParams;
	switch (action) {
		case 'DRAG': {
			const { dragSourceIndex, dragDestinationIndex } = reorderParams;
			const offset = dragDestinationIndex > dragSourceIndex ? 0 : 1;
			return toRawIndex(currentPageNumber, dragDestinationIndex) - offset;
		}
		case 'UP': {
			const sourceIndex = paginatedPrioritiesData.pages[currentPageNumber].items.findIndex(
				(priority) => priority.id === priorityId,
			);
			return toRawIndex(currentPageNumber, sourceIndex) - 2;
		}
		case 'DOWN': {
			const sourceIndex = paginatedPrioritiesData.pages[currentPageNumber].items.findIndex(
				(priority) => priority.id === priorityId,
			);
			return toRawIndex(currentPageNumber, sourceIndex) + 1;
		}
		case 'SET_POSITION': {
			const sourceRawIndex = toRawIndex(
				currentPageNumber,
				paginatedPrioritiesData.pages[currentPageNumber].items.findIndex(
					(priority) => priority.id === priorityId,
				),
			);
			const destinationRawIndex = reorderParams.position - 1;
			if (destinationRawIndex <= sourceRawIndex) {
				return destinationRawIndex - 1;
			}
			return destinationRawIndex;
		}
		default:
			throw new Error(`Failed to get the index of the previous item for ${action} action`);
	}
};

/**
 * Generates a move priorities request params and calculates the new page number based on the given parameters.
 * @param paginatedPrioritiesData - The paginated priorities data.
 * @param pageNumber - The current page number.
 * @param priorityId - The ID of the priority to be moved.
 * @param action - The action to perform (UP, DOWN, TOP, or BOTTOM).
 * @returns An object containing the move priorities requestParams and the new page number.
 * @throws Error if the action is invalid.
 */
export const getUpdatePriorityRequestParams = async (
	paginatedPrioritiesData: PaginatedPrioritiesData,
	currentPageNumber: number,
	reorderParams: ReorderParams,
	fetchPage?: (pageNumber: number) => Promise<Priority[]>,
): Promise<{
	requestParams: MovePrioritiesRequestParams;
	newPageNumber: number;
}> => {
	const { priorityId } = reorderParams;
	const sourceIndex = paginatedPrioritiesData.pages[currentPageNumber].items.findIndex(
		(priority) => priority.id === priorityId,
	);

	const rawIndexOfPreviousItem = getRawIndexOfPreviousItem({
		reorderParams,
		currentPageNumber,
		paginatedPrioritiesData,
	});

	// Handle moving the item to be the first in the list
	if (rawIndexOfPreviousItem < 0) {
		return generateMoveRequestParams({
			priorityId,
			position: 'First',
			newPageNumber: 1,
		});
	}

	// Otherwise convert the raw index to the page and position on the page and generate the required parameters
	const { pageNumber: previousItemPageNumber, pageIndex: previousItemPageIndex } =
		toPageNumberAndIndex(rawIndexOfPreviousItem);
	const rawIndexOfSource = toRawIndex(currentPageNumber, sourceIndex);
	const offset = rawIndexOfPreviousItem > rawIndexOfSource ? 0 : 1;
	const { pageNumber: newPageNumber } = toPageNumberAndIndex(rawIndexOfPreviousItem + offset);

	if (paginatedPrioritiesData.pages[newPageNumber]) {
		return generateMoveRequestParams({
			priorityId,
			after: paginatedPrioritiesData.pages[previousItemPageNumber].items[previousItemPageIndex].id,
			newPageNumber,
		});
	}

	if (fetchPage) {
		try {
			const items = await fetchPage(newPageNumber);
			return generateMoveRequestParams({
				priorityId,
				after: items[previousItemPageIndex].id,
				newPageNumber,
			});
		} catch (error) {
			throw new Error(`Failed to fetch destination page: ${error}`);
		}
	}

	throw new Error('Missing fetch function for reorder');
};

export const calculateAddPriorityPaginatedPage = (totalItems: number, pageSize: number) =>
	Math.ceil((totalItems + 1) / pageSize);
